import { useEffect, useState } from "react";
import { X, ImageOff, Maximize2 } from "lucide-react";
import { apiClient } from "../api/apiClient.js";

import { MediaSkeleton } from "../components/Skeleton.jsx";
/**
 * P157-R9: a stored file rendered as its real image, for any authenticated
 * "give me the bytes" route.
 *
 * PassportDocumentViewer already does this, but it is hardcoded to the passport
 * route and takes an applicationId. The worker photo and each extra document
 * are different paths on the same contract, so this takes the path instead.
 * Deliberately NOT a rewrite of that component: it is still the one the
 * applicant drawer and War Room use, and changing its signature would have
 * meant touching three screens to add a feature to one.
 *
 * Same rules as that component, for the same reasons:
 *  - fetches real bytes through the authenticated client, never an <img src>
 *    pointing at an API path (that request would carry no session token)
 *  - a 404 is an honest "nothing uploaded" empty state, never a placeholder
 *  - the object URL is created once per path and revoked on cleanup, so an
 *    operator paging through workers does not leak one blob URL per open
 */
export default function StoredFileImage({ path, label, present, onCleared }) {
  const [state, setState] = useState({ status: "idle", objectUrl: null, error: null });
  const [enlarged, setEnlarged] = useState(false);

  useEffect(() => {
    if (!path || !present) {
      setState({ status: "empty", objectUrl: null, error: null });
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    setState({ status: "loading", objectUrl: null, error: null });
    apiClient
      .getBlob(path)
      .then(({ blob }) => {
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setState({ status: "ready", objectUrl: createdUrl, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", objectUrl: null, error });
        }
      });
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL?.(createdUrl);
      }
    };
  }, [path, present]);

  // Closing the enlarged view must not also close the dialog underneath it:
  // both listen on document, so this consumes the key on the capture phase.
  // Same fix as PassportDocumentViewer carries.
  useEffect(() => {
    if (!enlarged) {
      return undefined;
    }
    function handleKeyDown(event) {
      if (event.key !== "Escape") {
        return;
      }
      event.stopImmediatePropagation();
      setEnlarged(false);
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [enlarged]);

  useEffect(() => {
    if (state.status === "empty" || (state.status === "error" && state.error?.status === 404)) {
      onCleared?.();
    }
  }, [state.status, state.error, onCleared]);

  if (state.status === "empty") {
    return (
      <div className="wx-wdp-file wx-wdp-file--empty">
        <ImageOff size={16} aria-hidden="true" />
        <span>ยังไม่มีไฟล์</span>
      </div>
    );
  }

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="wx-wdp-file wx-wdp-file--loading">
        <MediaSkeleton label="กำลังโหลดไฟล์..." height="100%" radius={8} />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wdp-file wx-wdp-file--empty">
        <ImageOff size={16} aria-hidden="true" />
        <span>
          {state.error?.status === 404 || state.error?.status === 403
            ? "ยังไม่มีไฟล์"
            : "โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"}
        </span>
      </div>
    );
  }

  return (
    <>
      <button type="button" className="wx-wdp-file wx-wdp-file--thumb" onClick={() => setEnlarged(true)}>
        <img src={state.objectUrl} alt={label} />
        <span className="wx-wdp-file-zoom">
          <Maximize2 size={13} aria-hidden="true" />
          ดูเต็ม
        </span>
      </button>

      {enlarged && (
        <div className="wx-passport-modal-backdrop" onClick={() => setEnlarged(false)}>
          <div
            className="wx-passport-modal"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wx-passport-modal-head">
              <strong>{label}</strong>
              <button type="button" className="wx-passport-modal-close" onClick={() => setEnlarged(false)} aria-label="ปิด">
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="wx-passport-modal-body">
              <img src={state.objectUrl} alt={label} className="wx-passport-modal-image" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
